import { func, object } from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { uniq } from "lodash";
import { MaxTimeModifiers, getExchangeRate } from "@repo/utils";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
} from "@tools";
import { Checkbox, Alert, Radio } from "@components";
import Grid from "@material-ui/core/Grid";

const RATE_ACTIONS = {
  LOAD: "loadRate",
  CONVERT: "convertRate",
  CHANGE: "changeRate",
};

export const DifferentSalesRateActionAlert = observer(({
 newElementData,
 onCancel,
 onAccept,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();

  const { hasSellerPrivileges } = useEstimateEditorSettings();

  const [ open, setOpen ] = useState(false);
  const [ selectedAction, setSelectedAction ] = useState(RATE_ACTIONS.CONVERT);
  const [ isLoading, setLoading ] = useState(false);

  const {
    defaultSalesCurrency,
    defaultCostCurrency,
    salesRateAlertLock,
    salesRateAlertAction,
    importRoleSalesRates,
    importRoleCostRates,
  } = editorStore;
  const { currency, companyCurrency } = structure.settings;

  useEffect(() => {
    if(!hasSellerPrivileges || !newElementData.salesRate)
      applySelectedAction(RATE_ACTIONS.LOAD);
    else if(salesRateAlertAction)
      applySelectedAction(salesRateAlertAction);
    else if(currency === defaultSalesCurrency || !importRoleSalesRates)
      applySelectedAction();
    else {
      setSelectedAction(RATE_ACTIONS.CONVERT)
      setOpen(true);
    }
  }, [ ]);

  const handleCancel = () => onCancel();

  const handleLock = () => {
    editorStore.switchSalesRateAlertLock()
  };

  const handleAccept = async () => {
    setLoading(true);
    if(salesRateAlertLock)
      editorStore.setSalesRateAlertAction(selectedAction);

    await applySelectedAction(selectedAction);
    setLoading(false);
  }

  const handleSelectAction = (e) => {
    const v = e.target.value;
    setSelectedAction(v);
  }

  const applySelectedAction = async (action) => {
    let salesExchangeRate = 1, costExchangeRate = 1;

    let costCurrency = companyCurrency;
    let salesCurrency = currency;

    if(action === RATE_ACTIONS.CHANGE && salesCurrency !== defaultSalesCurrency) {
      structure.settings.setCurrency(defaultSalesCurrency);
      salesCurrency = defaultSalesCurrency;
    }

    const costRate = newElementData.costRate || undefined;
    const salesRate = newElementData.salesRate || 0;
    const hasDifferentCostCurrency = (defaultCostCurrency !== costCurrency && importRoleCostRates)
    const hasDifferentSalesCurrency = (defaultSalesCurrency !== salesCurrency && importRoleSalesRates)

    if( hasDifferentSalesCurrency || hasDifferentCostCurrency ) {
      const rates = await editorStore.getExchangeRates(
        ...uniq([defaultCostCurrency, costCurrency, defaultSalesCurrency, salesCurrency].filter(c => c))
      );

      if(defaultCostCurrency !== costCurrency && costCurrency === salesCurrency) {
        structure.settings.setCompanyCurrency(defaultCostCurrency);
        structure.settings.setDailyExchangeRate(rates);
        costCurrency = defaultCostCurrency;
      }

      if(hasDifferentCostCurrency)
        costExchangeRate = getExchangeRate(rates, costCurrency, defaultCostCurrency);
      if(action === RATE_ACTIONS.CONVERT && hasDifferentSalesCurrency)
        salesExchangeRate = getExchangeRate(rates, salesCurrency, defaultSalesCurrency);
    }

    onAccept({
      ...newElementData,
      costRate: costRate && (costRate / MaxTimeModifiers.HOUR * costExchangeRate),
      salesRate: salesRate && (salesRate / MaxTimeModifiers.HOUR * salesExchangeRate),
    });
  }

  const translationCurrencies = {
    systemCurrency: defaultSalesCurrency,
    estimateCurrency: currency,
  }

  return (
    <Alert
      open={open}
      title={t("views.editor.dialogs.summary_add_currency_alert.title")}
      width={620}
      isAcceptLoading={isLoading}
      acceptText={t("views.editor.dialogs.summary_add_currency_alert.accept")}
      onCancel={handleCancel}
      onAccept={handleAccept}
    >
      <p>
        {t("views.editor.dialogs.summary_add_currency_alert.subtitle", translationCurrencies)}
      </p>
      <Grid
        item container
        direction="column"
        className="gap-2 mt-2 mb-4"
      >
        <p className="font-bold">
          {t("views.editor.dialogs.summary_add_currency_alert.button_group")}
        </p>
        <Radio
          name="currency-action"
          aria-label="currency-action"
          value={RATE_ACTIONS.CONVERT}
          checked={selectedAction === RATE_ACTIONS.CONVERT}
          onChange={handleSelectAction}
          label={t("views.editor.dialogs.summary_add_currency_alert.convert", translationCurrencies)}
        />
        <Radio
          name="currency-action"
          aria-label="currency-action"
          value={RATE_ACTIONS.LOAD}
          checked={selectedAction === RATE_ACTIONS.LOAD}
          onChange={handleSelectAction}
          label={t("views.editor.dialogs.summary_add_currency_alert.load", translationCurrencies)}
        />
        <Radio
          name="currency-action"
          aria-label="currency-action"
          value={RATE_ACTIONS.CHANGE}
          checked={selectedAction === RATE_ACTIONS.CHANGE}
          onChange={handleSelectAction}
          label={t("views.editor.dialogs.summary_add_currency_alert.change", translationCurrencies)}
        />
      </Grid>
      <Checkbox
        name="lockOption"
        checked={salesRateAlertLock}
        onChange={handleLock}
        label={t("common.remember_session_choice")}
      />
    </Alert>
  )
})

DifferentSalesRateActionAlert.propTypes = {
  onCancel: func.isRequired,
  onAccept: func.isRequired,
  newElementData: object.isRequired,
}